<script setup lang="ts">
import type { IFormBuilderReference } from '~/interfaces/form/formBuilder';
import { ArrowPathIcon } from '@heroicons/vue/24/outline';
import type { IMobileNumber } from '~/interfaces/dto/auth/response';
import Checkbox from 'primevue/checkbox';

const { $t, $trackEvent, $dayjs } = useNuxtApp();
const { $authService } = useServices();
const site = useSiteStore();
const auth = useAuthStore();

const form = ref<IFormBuilderReference>();
const otp = ref('');
const otpValid = ref(false);
const errorCode = ref('');
const errorRef = ref('');
const step = ref<1 | 2>(1);
const model = ref<{ newNumber: string }>({ newNumber: '' });
const loading = ref(false);
const checked = ref<boolean>(true);
const previouslyAddedNumber = ref<string>('');
const expiryDateTime = ref<string>($dayjs().add(3, 'minutes').format());
const resendLoading = ref(false);

const { expiryInterval, isDone, reset } = useCountDown({
  expiryDateTime: expiryDateTime.value,
  minutes: 3,
  daysBack: 1,
});

const preparedMobileNumber = computed(() =>
  model.value.newNumber ? usePrepareMobileNumber(model.value.newNumber) : '',
);

const disableNext = computed(() => {
  if (step.value === 1) return !(form.value?.validated && !form.value?.invalid);
  return !otpValid.value;
});

const setError = (displayCode?: unknown, refCode?: unknown) => {
  errorCode.value = String(displayCode ?? 'general');
  errorRef.value = refCode != null ? String(refCode) : '';
};

const goBack = () => {
  if (step.value === 1) {
    stop();
    site.activateModal('sumSubPasswordResetLoggedOutLivenessCheck');
  } else {
    step.value = 1;
    otp.value = '';
    errorCode.value = '';
    errorRef.value = '';
    model.value.newNumber = '';
  }
};

const addSecondaryNumber = async () => {
  loading.value = true;
  errorCode.value = '';
  errorRef.value = '';
  if (previouslyAddedNumber.value === preparedMobileNumber.value) {
    loading.value = false;
    step.value = 2;
    return;
  }
  const addNumberResponse = await $authService.addMobileNumber({
    mobileNumber: preparedMobileNumber.value,
  });

  if (addNumberResponse?.isSuccessful) {
    step.value = 2;
    expiryDateTime.value = $dayjs().add(3, 'minutes').format();
    $trackEvent('Account', 'MY_ACCOUNT_ADD_SECONDARY_NUMBER');
    previouslyAddedNumber.value = preparedMobileNumber.value;
    reset(expiryDateTime.value);
  } else
    setError(
      addNumberResponse?.error?.displayCode,
      addNumberResponse?.error?.code,
    );

  loading.value = false;
};

const changePrimaryNumber = async () => {
  loading.value = true;
  errorCode.value = '';
  errorRef.value = '';

  const changeNumberResponse = await $authService.changePrimaryNumber({
    newPrimaryMobileNumber: model.value.newNumber,
  });

  if (changeNumberResponse?.isSuccessful) {
    $trackEvent('Account', 'MY_ACCOUNT_CHANGE_PRIMARY_NUMBER');
    site.activateModal('sumSubPasswordResetLoggedOutChangeNumberSuccess');
  } else {
    setError(
      changeNumberResponse?.error?.displayCode,
      changeNumberResponse?.error?.code,
    );
  }

  loading.value = false;
};

const verifyOtp = async () => {
  loading.value = true;
  errorCode.value = '';
  errorRef.value = '';

  if (isDone.value) {
    loading.value = false;
    setError('otp-expired');
    return;
  }

  const verifyOtpResponse = await $authService.verifyOtp({
    mobileNumber: model.value.newNumber,
    otp: otp.value,
    isPrimary: false,
  });

  if (verifyOtpResponse?.isSuccessful) await changePrimaryNumber();
  else {
    setError(
      verifyOtpResponse?.error?.displayCode,
      verifyOtpResponse?.error?.code,
    );
    loading.value = false;
  }
};

const handleStepOne = async () => {
  const current = auth.currentUser;

  if (current.primaryMobileNumber.mobileNumber === model.value.newNumber) {
    errorCode.value = 'number-already-used';
    errorRef.value = '';
    return;
  }

  const hasNoSecondary = current.secondaryMobileNumbers.length === 0;
  const secondaryMatchingNew = current.secondaryMobileNumbers.find(
    (n: IMobileNumber) => n.mobileNumber === preparedMobileNumber.value,
  );
  const secondaryVerified = !!secondaryMatchingNew?.verified;

  if (hasNoSecondary) await addSecondaryNumber();
  else if (secondaryVerified) await changePrimaryNumber();
  else if (!!secondaryMatchingNew) await requestOtp(true);
  else await addSecondaryNumber();
};

const handleNext = async () => {
  if (disableNext.value) return;
  if (step.value === 1) await handleStepOne();
  else await verifyOtp();
};

const handleOtpChange = (valid: boolean, enteredOtp?: string) => {
  otpValid.value = valid;
  if (!!enteredOtp) otp.value = enteredOtp;
};

const requestOtp = async (showOtpInput = false) => {
  if (resendLoading.value) return;
  resendLoading.value = true;
  errorCode.value = '';
  errorRef.value = '';

  const requestOtpResponse = await $authService.requestNewOtp({
    mobileNumber: preparedMobileNumber.value,
  });

  if (requestOtpResponse?.isSuccessful) {
    expiryDateTime.value = $dayjs().add(3, 'minutes').format();
    reset(expiryDateTime.value);
    if (showOtpInput) step.value = 2;
  } else {
    setError(
      requestOtpResponse?.error?.displayCode,
      requestOtpResponse?.error?.code,
    );
  }

  resendLoading.value = false;
};
</script>

<template>
  <div>
    <div>
      <UserInterfaceGenericLoader v-if="loading" />
      <div
        v-else
        class="pt-4 px-4 flex flex-col gap-2"
        :class="{ 'pb-2': step === 2, 'pb-4': step === 1 }"
      >
        <span class="text-base-priority text-xs">
          {{ step === 1 ? $t('enter-your-mobile-number') : $t('enter-otp') }}
        </span>

        <UserInterfaceDynamicFormBuilder
          v-if="step === 1"
          ref="form"
          v-model="model"
          template-name="update number"
          @keydown.enter.stop.prevent="
            () => {
              if (!disableNext) handleNext();
            }
          "
        />
        <template v-else>
          <DesignSystemFormOtp
            :use-pseudo="false"
            @valid="handleOtpChange(true, $event)"
            @invalid="handleOtpChange(false)"
          />

          <div class="flex gap-1">
            <span
              :class="{ 'text-red-500': isDone, 'text-base-priority': !isDone }"
            >
              {{
                !isDone ? $t('code-will-expire') : $t('your-code-has-expired')
              }}
            </span>
            <span v-if="!isDone" class="text-primary-blue-100">
              {{ expiryInterval }}
            </span>
          </div>
          <div class="flex items-center gap-2 pointer-events-none">
            <Checkbox
              v-model="checked"
              input-id="newNumber"
              name="newNumber"
              binary
              :pt="{
                box: {
                  class:
                    'w-6 border rounded-md dark:border-dark-600 border-light-600 flex items-center justify-center pointer-events-none bg-primary-blue-gradient border-none',
                },
              }"
              :value="true"
            />
            <label class="text-base-priority" for="newNumber">
              {{ $t('notify-new-primary') }}
            </label>
          </div>
          <UserInterfaceModalActionBadge
            bg="bg-light-50 dark:bg-dark-700"
            :left-label="isDone ? 'no-reset-code' : 'resend-code-not-available'"
            :action-text="isDone ? 'try-again' : ''"
            :icon-name="isDone ? ArrowPathIcon : null"
            :disabled="!isDone || resendLoading"
            @action="requestOtp"
          />
        </template>

        <UserInterfaceGenericError v-if="errorCode" :reference-code="errorRef">
          <span>{{ $t(`update-number-error-${errorCode}`) }}</span>
        </UserInterfaceGenericError>
      </div>

      <div v-if="!loading" class="p-4 bg-base flex gap-2">
        <DesignSystemButton
          class="w-full items-center justify-center"
          rounding="md"
          type="tertiary"
          @click="goBack"
        >
          {{ $t('back') }}
        </DesignSystemButton>

        <DesignSystemButton
          class="w-full items-center justify-center"
          rounding="md"
          type="primary"
          :disabled="(step === 2 && !otpValid && !isDone) || loading"
          @click="handleNext"
        >
          {{ $t('next') }}
        </DesignSystemButton>
      </div>
    </div>
  </div>
</template>
